class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this._scrollY = 0;
    this._bodyLocked = false;

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  /**
   * iOS-safe body scroll lock.
   * Uses position:fixed on <body> — the only reliable method on iOS Safari,
   * which ignores overflow:hidden for touch-driven scrolling.
   * Same proven technique used by opj-scroll-lock.js in this theme.
   */
  _lockBodyScroll() {
    if (this._bodyLocked) return;
    this._bodyLocked = true;
    this._scrollY = window.pageYOffset || document.documentElement.scrollTop;
    document.body.style.position = 'fixed';
    document.body.style.top = '-' + this._scrollY + 'px';
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.overflow = 'hidden';
  }

  _unlockBodyScroll() {
    if (!this._bodyLocked) return;
    this._bodyLocked = false;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.overflow = '';
    window.scrollTo(0, this._scrollY);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    this._lockBodyScroll();
  }

  close() {
    this._pendingEnd();
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    this._unlockBodyScroll();
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this._pendingEnd();
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  // Momavo instant cart: open the drawer with a loading shimmer the moment an add-to-cart that
  // renders this drawer starts. The existing add code still fills it (renderContents) or shows its error.
  _pendingStart() {
    clearTimeout(this._pendingTimer);
    this.classList.add('mcd-loading');
    this.open();
    this._pendingTimer = setTimeout(() => this._pendingEnd(), 10000);
  }

  _pendingEnd() {
    clearTimeout(this._pendingTimer);
    clearTimeout(this._pendingFallback);
    this.classList.remove('mcd-loading');
  }

  _pendingDone(ok) {
    if (!this.classList.contains('mcd-loading')) return;
    if (!ok) {
      this.close();
      return;
    }
    // Added but nothing rendered the drawer yet: load the fresh cart ourselves.
    this._pendingFallback = setTimeout(() => {
      if (!this.classList.contains('mcd-loading')) return;
      fetch(`${window.Shopify && Shopify.routes ? Shopify.routes.root : '/'}?sections=cart-drawer,cart-icon-bubble`)
        .then((r) => r.json())
        .then((sections) => this.renderContents({ sections }))
        .catch(() => this._pendingEnd());
    }, 2500);
  }
}

customElements.define('cart-drawer', CartDrawer);

(function () {
  if (!window.fetch || window.__mcdInstantCart) return;
  window.__mcdInstantCart = true;
  const origFetch = window.fetch;
  const rendersDrawer = (body) => {
    try {
      if (typeof body === 'string') return body.indexOf('cart-drawer') !== -1;
      if (body instanceof FormData) return String(body.get('sections') || '').indexOf('cart-drawer') !== -1;
    } catch (e) {}
    return false;
  };
  window.fetch = function (input, init) {
    const promise = origFetch.apply(this, arguments);
    try {
      const url = typeof input === 'string' ? input : '';
      const drawer = document.querySelector('cart-drawer');
      if (
        drawer &&
        drawer._pendingStart &&
        /\/cart\/add(\.js)?(\?|$)/.test(url) &&
        init && String(init.method || '').toUpperCase() === 'POST' &&
        rendersDrawer(init.body) &&
        !drawer.classList.contains('active') &&
        !(document.activeElement && document.activeElement.closest && document.activeElement.closest('quick-add-modal'))
      ) {
        drawer._pendingStart();
        promise.then((r) => drawer._pendingDone(r.ok), () => drawer._pendingDone(false));
      }
    } catch (e) {}
    return promise;
  };
})();

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
